import {
  type DposCryptoFlow,
  type DposSessionMode,
} from "../../services/dposService";

export type DposCreatePaymentStep =
  | "amount"
  | "method"
  | "88pay"
  | "crypto-option"
  | "crypto-session"
  | "paylink-session";

export interface PersistedCreatePaymentFlowState {
  amount: string;
  step: DposCreatePaymentStep;
  cryptoFlow: DposCryptoFlow | null;
}

export interface PersistedDposSessionState {
  sessionId: string;
  completedView: "success" | "receipt";
  selectedCryptoAsset: string | null;
}

const DPOS_CREATE_PAYMENT_FLOW_STORAGE_NAME = "dpos-flow-state";
const DPOS_SESSION_STORAGE_NAMESPACE = "dpos-session-view-state";

function normalizeAmountForStorageKey(amount: string): string {
  const trimmedAmount = amount.trim();
  const parsedAmount = Number.parseFloat(trimmedAmount);

  if (Number.isFinite(parsedAmount)) {
    return parsedAmount.toFixed(2);
  }

  return trimmedAmount || "no-amount";
}

function isSessionStorageAvailable(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.sessionStorage !== "undefined"
  );
}

function parseStoredValue<T>(value: string | null): T | null {
  if (!value) {
    return null;
  }

  try {
    return JSON.parse(value) as T;
  } catch {
    return null;
  }
}

export function readCreatePaymentFlowState(): PersistedCreatePaymentFlowState | null {
  if (!isSessionStorageAvailable()) {
    return null;
  }

  const storedValue = parseStoredValue<PersistedCreatePaymentFlowState>(
    window.sessionStorage.getItem(DPOS_CREATE_PAYMENT_FLOW_STORAGE_NAME)
  );

  if (!storedValue) {
    return null;
  }

  if (
    typeof storedValue.amount !== "string" ||
    typeof storedValue.step !== "string" ||
    (storedValue.cryptoFlow !== null &&
      storedValue.cryptoFlow !== "internal_wallet" &&
      storedValue.cryptoFlow !== "external_wallet")
  ) {
    return null;
  }

  return storedValue;
}

export function persistCreatePaymentFlowState(
  state: PersistedCreatePaymentFlowState
): void {
  if (!isSessionStorageAvailable()) {
    return;
  }

  window.sessionStorage.setItem(
    DPOS_CREATE_PAYMENT_FLOW_STORAGE_NAME,
    JSON.stringify(state)
  );
}

export function clearCreatePaymentFlowState(): void {
  if (!isSessionStorageAvailable()) {
    return;
  }

  window.sessionStorage.removeItem(DPOS_CREATE_PAYMENT_FLOW_STORAGE_NAME);
}

export function getDposSessionStorageKey(
  businessId: string | null,
  mode: DposSessionMode,
  amount: string
): string {
  return `${DPOS_SESSION_STORAGE_NAMESPACE}:${
    businessId || "no-business"
  }:${mode}:${normalizeAmountForStorageKey(amount)}`;
}

export function readDposSessionState(
  storageKey: string
): PersistedDposSessionState | null {
  if (!isSessionStorageAvailable()) {
    return null;
  }

  const storedValue = parseStoredValue<PersistedDposSessionState>(
    window.sessionStorage.getItem(storageKey)
  );

  if (!storedValue) {
    return null;
  }

  if (
    typeof storedValue.sessionId !== "string" ||
    (storedValue.completedView !== "success" &&
      storedValue.completedView !== "receipt") ||
    (storedValue.selectedCryptoAsset !== null &&
      typeof storedValue.selectedCryptoAsset !== "string")
  ) {
    return null;
  }

  return storedValue;
}

export function persistDposSessionState(
  storageKey: string,
  state: PersistedDposSessionState
): void {
  if (!isSessionStorageAvailable()) {
    return;
  }

  window.sessionStorage.setItem(storageKey, JSON.stringify(state));
}

export function clearDposSessionState(storageKey: string): void {
  if (!isSessionStorageAvailable()) {
    return;
  }

  window.sessionStorage.removeItem(storageKey);
}
